// C:\Users\abdul\Downloads\Onebox\onebox\src\App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import AboutUs from './components/AboutUs';
import Stats from './components/Stats';
import TodaysSpecial from './components/TodaysSpecial';
import Menu from './components/Menu';
import MeetChef from './components/MeetChef';
import Testimonials from './components/Testimonials';
import RestaurantProcess from './components/RestaurantProcess';
import Footer from './components/Footer';
import CartDrawer from './components/CartDrawer';
import OrderTracker from './components/OrderTracker';
import { useCart } from './hooks/useCart';
import './App.css';

// ── Root Router ───────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Main glanexa.me landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Admin panel — must be BEFORE /:slug to avoid clash */}
        <Route path="/admin/*" element={<AdminPlaceholder />} />

        {/* Tenant route — glanexa.me/shwarmahouse */}
        <Route path="/:slug/*" element={<TenantApp />} />
      </Routes>
    </BrowserRouter>
  );
}

// ── Placeholder pages (replace with real components later) ───────────────────
function LandingPage() {
  return (
    <div className="landing-screen">
      <div className="landing-content">
        <h1>Welcome to Glanexa</h1>
        <p>Restaurant management platform</p>
      </div>
    </div>
  );
}

function AdminPlaceholder() {
  return (
    <div className="landing-screen">
      <h1>Admin Panel</h1>
    </div>
  );
}

// ── Tenant App — loaded per slug ──────────────────────────────────────────────
function TenantApp() {
  const { slug } = useParams(); // e.g. "shwarmahouse" from glanexa.me/shwarmahouse

  const [tenant, setTenant]             = useState(null);
  const [sections, setSections]         = useState({});
  const [menuItems, setMenuItems]       = useState([]);
  const [categories, setCategories]     = useState([]);
  const [teamMembers, setTeamMembers]   = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const cart = useCart(tenant?.id);

  useEffect(() => {
    loadRestaurantData();
  }, [slug]); // re-run if slug changes

  // ── Dynamic meta tags ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenant) return;

    document.title = `${tenant.business_name} | Fine Dining Experience`;

    const setMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content',
      tenant.description || `Experience authentic cuisine at ${tenant.business_name}`
    );
    if (tenant.primary_color) {
      setMeta('meta[name="theme-color"]', 'content', tenant.primary_color);
    }
    setMeta('meta[property="og:title"]',       'content', tenant.business_name);
    setMeta('meta[property="og:description"]', 'content',
      tenant.description || `Experience authentic cuisine at ${tenant.business_name}`
    );
    if (tenant.logo_url) {
      setMeta('meta[property="og:image"]', 'content', tenant.logo_url);
    }
  }, [tenant]);

  // ── Tenant loaders ──────────────────────────────────────────────────────────
  const loadTenantBySubdomain = async (subdomain) => {
    return await supabase
      .from('tenants')
      .select('*')
      .eq('subdomain', subdomain)
      .in('status', ['active', 'trial'])
      .is('deleted_at', null)
      .single();
  };

  const loadTenantByCustomDomain = async (domain) => {
    return await supabase
      .from('tenants')
      .select('*')
      .eq('custom_domain', domain)
      .eq('domain_verified', true)
      .in('status', ['active', 'trial'])
      .is('deleted_at', null)
      .single();
  };

  // ── Resolve tenant from URL ─────────────────────────────────────────────────
  const resolveTenant = async () => {
    const hostname = window.location.hostname;
    console.log('🌐 Hostname:', hostname, '| Slug:', slug);

    // 1. Localhost / dev — use slug from path
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      const devSlug = slug || 'shwarmahouse';
      console.log('🔍 Localhost — using path slug:', devSlug);
      return await loadTenantBySubdomain(devSlug);
    }

    // 2. glanexa.me/pizza or pages.dev/pizza — path-based routing (Phase 1)
    if (
      hostname === 'glanexa.me' ||
      hostname === 'www.glanexa.me' ||
      hostname.includes('pages.dev')
    ) {
      console.log('🔍 Path routing — slug:', slug);
      return await loadTenantBySubdomain(slug);
    }

    // 3. pizza.glanexa.me — legacy subdomain format (keep for backward compat)
    if (hostname.split('.').length >= 3 && hostname.includes('glanexa')) {
      const subdomain = hostname.split('.')[0];
      console.log('🔍 Subdomain detected:', subdomain);
      return await loadTenantBySubdomain(subdomain);
    }

    // 4. pizzapalace.com — custom domain (Phase 2 via Worker)
    console.log('🔍 Custom domain:', hostname);
    const { data: customData, error: customError } = await loadTenantByCustomDomain(hostname);
    if (!customError && customData) {
      console.log('✅ Matched by custom_domain');
      return { data: customData, error: null };
    }

    // 5. Fallback — try hostname first part as subdomain
    console.log('⚠️ Fallback subdomain attempt:', hostname.split('.')[0]);
    return await loadTenantBySubdomain(hostname.split('.')[0]);
  };

  // ── Load all restaurant data in parallel ───────────────────────────────────
  const loadRestaurantData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: tenantData, error: tenantError } = await resolveTenant();

      if (tenantError || !tenantData) {
        console.error('❌ Tenant error:', tenantError);
        setError(
          tenantError?.code === 'PGRST116'
            ? `Restaurant "${slug}" not found. Please check the URL.`
            : 'Unable to load restaurant data.'
        );
        setLoading(false);
        return;
      }

      console.log('✅ Tenant loaded:', tenantData.business_name);
      setTenant(tenantData);
      const tid = tenantData.id;

      const [
        sectionsRes,
        categoriesRes,
        menuRes,
        teamRes,
        testimonialsRes,
      ] = await Promise.all([
        supabase
          .from('website_sections')
          .select('*')
          .eq('tenant_id', tid)
          .eq('is_published', true)
          .eq('is_visible', true)
          .order('display_order'),

        supabase
          .from('menu_categories')
          .select('*')
          .eq('tenant_id', tid)
          .eq('is_active', true)
          .order('display_order'),

        supabase
          .from('menu_items')
          .select('*, menu_categories (id, name, slug)')
          .eq('tenant_id', tid)
          .eq('is_available', true)
          .order('display_order'),

        supabase
          .from('team_members')
          .select('*')
          .eq('tenant_id', tid)
          .eq('is_active', true)
          .is('deleted_at', null)
          .order('display_order'),

        supabase
          .from('testimonials')
          .select('*')
          .eq('tenant_id', tid)
          .eq('is_approved', true)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .limit(10),
      ]);

      if (!sectionsRes.error) {
        const sectionsMap = {};
        (sectionsRes.data || []).forEach(s => { sectionsMap[s.section_name] = s.content; });
        setSections(sectionsMap);
        console.log('✅ Sections:', Object.keys(sectionsMap).length);
      }

      if (!categoriesRes.error) { setCategories(categoriesRes.data || []); }
      if (!menuRes.error)        { setMenuItems(menuRes.data || []); }
      if (!teamRes.error)        { setTeamMembers(teamRes.data || []); }
      if (!testimonialsRes.error){ setTestimonials(testimonialsRes.data || []); }

      console.log('🎉 All data loaded!');

    } catch (err) {
      console.error('❌ Fatal error:', err);
      setError('Failed to load restaurant data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Loading Screen ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <h2>Loading Restaurant...</h2>
          <p>Please wait while we prepare your dining experience</p>
        </div>
      </div>
    );
  }

  // ── Error Screen ────────────────────────────────────────────────────────────
  if (error || !tenant) {
    return (
      <div className="error-screen">
        <div className="error-content">
          <div className="error-icon">🍽️</div>
          <h1>Oops! Something went wrong</h1>
          <p className="error-message">
            {error || 'This restaurant does not exist or is not active.'}
          </p>
          <div className="error-actions">
            <button className="retry-button" onClick={() => window.location.reload()}>
              🔄 Try Again
            </button>
            <a href="/" className="home-button">🏠 Go to Homepage</a>
          </div>
          <p className="error-help">
            Need help? Contact support at{' '}
            <a href="mailto:support@glanexa.me">support@glanexa.me</a>
          </p>
        </div>
      </div>
    );
  }

  // ── Main Restaurant App ─────────────────────────────────────────────────────
  return (
    <div
      className="app"
      style={{
        '--brand-primary':   tenant.primary_color   || '#FF9F0D',
        '--brand-secondary': tenant.secondary_color || '#FFFFFF',
        '--brand-font':      tenant.font_family     || 'Inter',
      }}
    >
      <Navbar
        tenant={tenant}
        sections={sections}
        cartCount={cart.totalItems}
        onOpenCart={() => cart.setIsOpen(true)}
      />

      <div id="home">
        {sections.hero && <Hero tenant={tenant} data={sections.hero} />}
      </div>

      <div id="about">
        {sections.about && <AboutUs tenant={tenant} data={sections.about} />}
      </div>

      {sections.special && menuItems.length > 0 && (
        <TodaysSpecial
          tenant={tenant}
          menuItems={menuItems}
          data={sections.special}
          onAddToCart={cart.addToCart}
          onOpenCart={() => cart.setIsOpen(true)}
        />
      )}

      {sections.stats && <Stats tenant={tenant} data={sections.stats} />}

      <div id="menu">
        {menuItems.length > 0 && (
          <Menu
            tenant={tenant}
            menuItems={menuItems}
            categories={categories}
            data={sections.menu}
            onAddToCart={cart.addToCart}
            onOpenCart={() => cart.setIsOpen(true)}
          />
        )}
      </div>

      {teamMembers.length > 0 && (
        <MeetChef tenant={tenant} teamMembers={teamMembers} data={sections.team} />
      )}

      <div id="testimonials">
        <Testimonials
          tenant={tenant}
          testimonials={testimonials}
          data={sections.testimonials}
        />
      </div>

      {sections.process && (
        <RestaurantProcess tenant={tenant} data={sections.process} />
      )}

      <div id="footer">
        <Footer tenant={tenant} contactData={sections.contact} />
      </div>

      <CartDrawer
        isOpen={cart.isOpen}
        onClose={() => cart.setIsOpen(false)}
        cartItems={cart.cartItems}
        totalItems={cart.totalItems}
        subtotal={cart.subtotal}
        tax={cart.tax}
        total={cart.total}
        onUpdateQuantity={cart.updateQuantity}
        onRemoveItem={cart.removeItem}
        onPlaceOrder={cart.placeOrder}
        loading={cart.loading}
        tenant={tenant}
      />

      <OrderTracker
        activeOrder={cart.activeOrder}
        tenant={tenant}
        onLookup={cart.lookupOrderByPhone}
        onDismiss={() => cart.setActiveOrder(null)}
      />
    </div>
  );
}

export default App;